import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { updateViewerUser, type User } from '@/entities/viewer';
import { api } from '@/shared/api';

export interface ProfileValues {
  login: string;
  email: string;
  display_name: string;
  bio: string;
  locale: 'ru' | 'en';
  timezone: string;
  first_day_of_week: number;
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: Partial<ProfileValues>) => api.patch<User>('/v1/account', values),
    onSuccess: (user) => {
      updateViewerUser(client, user);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (values: { current_password: string; new_password: string }) =>
      api.post('/v1/account/password', values),
  });
}

export interface AccountSession {
  id: string;
  created_at: string;
  expires_at: string;
  /** Last authenticated request (recorded every few minutes), or null when never recorded. */
  last_active_at: string | null;
  user_agent: string | null;
  is_current: boolean;
}

const sessionsKey = ['account', 'sessions'] as const;

export function useSessions() {
  return useQuery({
    queryKey: sessionsKey,
    queryFn: ({ signal }) => api.get<AccountSession[]>('/v1/auth/sessions', { signal }),
  });
}

/** Revoke one session server-side; when it is the current one the cookie is cleared too. */
export function useRevokeSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/auth/sessions/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: sessionsKey }),
  });
}

export function useRevokeOtherSessions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ revoked: number }>('/v1/auth/sessions/revoke-others'),
    onSuccess: () => client.invalidateQueries({ queryKey: sessionsKey }),
  });
}
